import { Alert, Button } from '@runnerboy/ui';
import {
  ArrowRight,
  BadgeCheck,
  CalendarX2,
  Car,
  HeartPulse,
  Home,
  IdCard,
  LifeBuoy,
  MapPinned,
  Phone,
  ShieldCheck,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { SignatureHero } from '../components/hero';
import { Page } from '../components/shell';

function Point({
  n,
  icon,
  title,
  children,
}: {
  n: number;
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className="flex gap-3.5 rounded-[22px] bg-white p-4 shadow-[var(--shadow-card)]">
      <span className="flex shrink-0 flex-col items-center gap-1.5">
        <span className="grid h-11 w-11 place-items-center rounded-[14px] bg-night-900 text-marigold-500">
          {icon}
        </span>
        <span className="rb-eyebrow text-[9.5px] text-ink-400">{String(n).padStart(2, '0')}</span>
      </span>
      <span className="min-w-0">
        <span className="block text-[15.5px] font-bold text-ink-900">{title}</span>
        <span className="mt-1 block text-[13.5px] leading-relaxed text-ink-600">{children}</span>
      </span>
    </li>
  );
}

/**
 * The road-safety initiative. Wording rules: no government logos, no claim of endorsement or authorisation that has
 * not been granted, and no outcome statistics we have not measured.
 */
export function RoadSafetyPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  return (
    <>
      <div className="bg-night-900 px-4 pb-12 pt-3">
        <div className="mx-auto max-w-3xl">
          <h1 className="sr-only">{t('safety.title', 'Road safety')}</h1>
          <SignatureHero linked={false} />
        </div>
      </div>
      <Page className="relative -mt-8 rounded-t-[32px] bg-paper pt-7">
        <section>
          <div className="rb-eyebrow text-ink-500">{t('safety.idea', 'The idea')}</div>
          <h2 className="rb-display mt-1 text-[26px] font-bold leading-tight text-ink-900">
            {t('safety.whyTitle', 'Why Runner Boy exists')}
          </h2>
          <p className="mt-2.5 text-[15px] leading-relaxed text-ink-700">
            {t(
              'safety.whyBody',
              'Too many trips to a liquor shop happen after people have already been drinking — often by car or two-wheeler. Runner Boy removes that trip. Your neighbourhood licensed store packs and seals the order, and a verified runner brings it to your door, so nobody needs to get behind the wheel.',
            )}
          </p>
        </section>

        <ul className="mt-6 flex flex-col gap-2.5">
          <Point n={1} icon={<Home className="h-5 w-5" />} title={t('safety.p1t', 'Stay where you are')}>
            {t('safety.p1', 'Order from home, a friend’s place or your hotel room. The store comes to you.')}
          </Point>
          <Point
            n={2}
            icon={<Car className="h-5 w-5" />}
            title={t('safety.p2t', 'Keep the keys on the hook')}
          >
            {t(
              'safety.p2',
              'Driving after drinking is an offence under the Motor Vehicles Act. If you have had a drink, don’t drive — and don’t let friends drive.',
            )}
          </Point>
          <Point n={3} icon={<IdCard className="h-5 w-5" />} title={t('safety.p3t', 'Age is checked twice')}>
            {t(
              'safety.p3',
              'You confirm your age at checkout, and your runner checks an original ID at the door. No ID, no alcohol — non-alcoholic items can still be handed over.',
            )}
          </Point>
          <Point
            n={4}
            icon={<HeartPulse className="h-5 w-5" />}
            title={t('safety.p4t', 'Responsible service')}
          >
            {t(
              'safety.p4',
              'Runners may refuse to hand over alcohol to anyone who appears intoxicated, underage or unable to verify their identity.',
            )}
          </Point>
          <Point
            n={5}
            icon={<CalendarX2 className="h-5 w-5" />}
            title={t('safety.p5t', 'Local rules, applied automatically')}
          >
            {t(
              'safety.p5',
              'Dry days, sale hours, quantity limits and state borders are enforced for every order. Alcohol never crosses a state line.',
            )}
          </Point>
          <Point
            n={6}
            icon={<MapPinned className="h-5 w-5" />}
            title={t('safety.p6t', 'Licensed stores only')}
          >
            {t(
              'safety.p6',
              'Every order is sold by a licensed neighbourhood retailer. Runner Boy provides the technology and delivery.',
            )}
          </Point>
        </ul>

        <section className="mt-7">
          <div className="rb-eyebrow text-ink-500">{t('safety.howEyebrow', 'How a safe delivery works')}</div>
          <h2 className="rb-display mt-1 text-[24px] font-bold leading-tight text-ink-900">{t('safety.howTitle', 'From a licensed shelf to your door')}</h2>
          <ol className="mt-4 flex flex-col gap-2.5">
            {[
              [t('safety.s1t', 'You stay put'), t('safety.s1', 'Order from home, a friend’s place or your hotel. Your age is confirmed before checkout.')],
              [t('safety.s2t', 'Rules are checked'), t('safety.s2', 'Sale hours, dry days, quantity limits and state borders are applied before the order is accepted.')],
              [t('safety.s3t', 'A licensed store packs and seals'), t('safety.s3', 'The store prepares the order and seals the bag with a tamper-evident label.')],
              [t('safety.s4t', 'A verified runner collects it'), t('safety.s4', 'The runner scans the store’s pickup code and confirms the seal; you can follow the delivery live.')],
              [t('safety.s5t', 'Original ID at the door'), t('safety.s5', 'The runner checks an original ID in person and asks for your delivery code. No ID, no alcohol.')],
            ].map(([title, body], i) => (
              <li key={title} className="flex gap-3.5 rounded-[22px] bg-white p-4 shadow-[var(--shadow-card)]">
                <span className="rb-display rb-num grid h-10 w-10 shrink-0 place-items-center rounded-full bg-marigold-500 text-[16px] font-bold text-ink-950">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block text-[15.5px] font-bold text-ink-900">{title}</span>
                  <span className="mt-1 block text-[13.5px] leading-relaxed text-ink-600">{body}</span>
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section className="relative mt-6 overflow-hidden rounded-[26px] bg-night-900 p-5 text-white">
          <span
            className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full bg-marigold-500/20 blur-3xl"
            aria-hidden
          />
          <div className="rb-eyebrow text-marigold-500">{t('safety.tonight', 'Tonight')}</div>
          <h2 className="rb-display mt-1 text-[21px] font-bold">
            {t('safety.tipsTitle', 'If you are drinking tonight')}
          </h2>
          <ul className="mt-3 space-y-2.5 text-[14px] leading-relaxed text-white/80 [&>li]:relative [&>li]:pl-5 [&>li]:before:absolute [&>li]:before:left-0 [&>li]:before:top-[0.6em] [&>li]:before:h-1.5 [&>li]:before:w-1.5 [&>li]:before:rounded-full [&>li]:before:bg-marigold-500">
            <li>
              {t(
                'safety.tip1',
                'Decide how you will get home before you start — a cab, a sober driver, or simply staying in.',
              )}
            </li>
            <li>
              {t(
                'safety.tip2',
                'Eat something and keep water nearby. We deliver water, soda and snacks too.',
              )}
            </li>
            <li>
              {t('safety.tip3', 'Look after your friends. Take their keys if they have been drinking.')}
            </li>
          </ul>
        </section>

        <section className="mt-6 grid grid-cols-2 gap-3">
          <a
            href="tel:112"
            className="rb-press flex items-center gap-3 rounded-[22px] bg-danger-600 p-4 text-white shadow-[var(--shadow-card)]"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/15">
              <Phone className="h-5 w-5" aria-hidden />
            </span>
            <span>
              <span className="rb-eyebrow block text-[10px] text-white/80">
                {t('safety.emergency', 'Emergency')}
              </span>
              <span className="rb-display rb-num block text-[24px] font-extrabold leading-none">112</span>
            </span>
          </a>
          <button
            onClick={() => nav('/support')}
            className="rb-press flex items-center gap-3 rounded-[22px] bg-white p-4 text-left shadow-[var(--shadow-card)]"
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ink-900/[0.05] text-ink-800">
              <LifeBuoy className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[14px] font-bold leading-tight text-ink-900">
              {t('nav.support', 'Help & support')}
            </span>
          </button>
        </section>

        <section className="mt-6 rounded-[22px] bg-white p-5 ring-1 ring-ink-900/[0.07]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-peacock-600" aria-hidden />
            <h2 className="text-[15px] font-bold text-ink-900">
              {t('safety.statusTitle', 'Initiative status')}
            </h2>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">
            {t(
              'safety.status',
              'Runner Boy is a private road-safety initiative. We intend to operate with the appropriate government authorisations in each state and city, and alcohol delivery is switched on only where the required licences and approvals are documented. No government body has endorsed Runner Boy unless stated here with the approving authority’s name.',
            )}
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
            <BadgeCheck className="h-4 w-4" aria-hidden />{' '}
            {t('safety.noEndorsement', 'Currently: no government endorsement claimed.')}
          </div>
        </section>

        <Alert tone="info" className="mt-6">
          {t(
            'safety.age',
            'Alcohol is sold only to adults of the legal drinking age for your state. Please drink responsibly.',
          )}
        </Alert>

        <Button block size="lg" variant="accent" className="mt-6" onClick={() => nav('/')}>
          {t('safety.cta', 'Order from your place')} <ArrowRight className="h-5 w-5" />
        </Button>
      </Page>
    </>
  );
}
